import type { CountryProfile } from '@/types';

// ---------------------------------------------------------------------------
// Country Intelligence Profiles
// Sources: SIPRI, IISS Military Balance 2024-2025, World Bank, GFP Index
// Updated: March 2026
// ---------------------------------------------------------------------------

export const COUNTRY_PROFILES: CountryProfile[] = [
  // ── P5 + Major Powers ─────────────────────────────────────────────────────
  {
    code: 'US', name: 'United States', nameKo: '미국',
    flagEmoji: '🇺🇸', capitalCity: 'Washington D.C.',
    capitalLon: -77.04, capitalLat: 38.91,
    population: 335, gdpBillionUSD: 28780, gdpPerCapita: 85373, gdpGrowthPct: 2.8,
    military: {
      activeTroops: 1328, reserveTroops: 799,
      aircraftCarriers: 11, submarines: 68, destroyersFrigates: 92,
      combatAircraft: 13247, tanks: 5500, artilleryPieces: 1339,
      navalVesselsTotal: 484, budgetBillionUSD: 886, globalFirepowerRank: 1,
    },
    nuclearStatus: 'possesses',
    leader: {
      name: 'Donald Trump', nameKo: '도널드 트럼프', title: 'President',
      sinceYear: 2025, age: 79,
      riskLevel: 'medium',
      riskNotes: '2nd term; NATO burden-sharing pressure; Ukraine aid uncertain; trade war escalation',
      successionRisk: 'low',
    },
    alliances: ['NATO', 'G7', 'G20', 'QUAD', 'AUKUS', 'Five Eyes', 'UN P5'],
    geopoliticalStance: 'western',
    strategicObjectives: [
      'Contain China in Indo-Pacific (QUAD/AUKUS)',
      'Maintain NATO deterrence vs Russia',
      'Prevent Iran nuclear breakout',
      'Preserve dollar hegemony',
      '"America First" trade realignment',
    ],
    instabilityRisk: 'low', powerIndex: 100,
  },
  {
    code: 'CN', name: 'China', nameKo: '중국',
    flagEmoji: '🇨🇳', capitalCity: 'Beijing',
    capitalLon: 116.39, capitalLat: 39.91,
    population: 1409, gdpBillionUSD: 18530, gdpPerCapita: 13136, gdpGrowthPct: 4.9,
    military: {
      activeTroops: 2035, reserveTroops: 510,
      aircraftCarriers: 3, submarines: 79, destroyersFrigates: 88,
      combatAircraft: 3285, tanks: 5000, artilleryPieces: 3800,
      navalVesselsTotal: 730, budgetBillionUSD: 296, globalFirepowerRank: 3,
    },
    nuclearStatus: 'possesses',
    leader: {
      name: 'Xi Jinping', nameKo: '시진핑', title: 'General Secretary / President',
      sinceYear: 2012, age: 72,
      riskLevel: 'medium',
      riskNotes: 'Third term secured. Taiwan reunification top priority. Economic headwinds: property crisis, youth unemployment. Anti-corruption purges ongoing.',
      successionRisk: 'high',
    },
    alliances: ['SCO', 'BRICS', 'G20', 'UN P5'],
    geopoliticalStance: 'revisionist',
    strategicObjectives: [
      'Taiwan reunification (by 2049 or earlier)',
      'South China Sea dominance',
      'Belt and Road global expansion',
      'USD displacement (petroyuan)',
      'Technology self-sufficiency (chips, AI)',
    ],
    instabilityRisk: 'medium', powerIndex: 92,
  },
  {
    code: 'RU', name: 'Russia', nameKo: '러시아',
    flagEmoji: '🇷🇺', capitalCity: 'Moscow',
    capitalLon: 37.62, capitalLat: 55.75,
    population: 144, gdpBillionUSD: 2240, gdpPerCapita: 15560, gdpGrowthPct: 3.6,
    military: {
      activeTroops: 1320, reserveTroops: 2000,
      aircraftCarriers: 1, submarines: 58, destroyersFrigates: 14,
      combatAircraft: 4255, tanks: 12950, artilleryPieces: 6574,
      navalVesselsTotal: 598, nuclearWarheads: 5580, budgetBillionUSD: 109, globalFirepowerRank: 2,
    },
    nuclearStatus: 'possesses',
    leader: {
      name: 'Vladimir Putin', nameKo: '블라디미르 푸틴', title: 'President',
      sinceYear: 2000, age: 73,
      riskLevel: 'critical',
      riskNotes: '5th term. War crimes indictment (ICC). Ukraine war dragging. Prigozhin mutiny legacy. Health speculation. Increasingly isolated internationally.',
      successionRisk: 'high',
    },
    alliances: ['CSTO', 'SCO', 'UN P5', 'BRICS'],
    geopoliticalStance: 'revisionist',
    strategicObjectives: [
      'Subjugate Ukraine; establish buffer states',
      'Fracture NATO cohesion',
      'Maintain nuclear deterrence/coercion',
      'Circumvent Western sanctions via China/India',
      'Restore Soviet-era sphere of influence',
    ],
    instabilityRisk: 'high', powerIndex: 78,
  },
  {
    code: 'GB', name: 'United Kingdom', nameKo: '영국',
    flagEmoji: '🇬🇧', capitalCity: 'London',
    capitalLon: -0.13, capitalLat: 51.51,
    population: 67, gdpBillionUSD: 3089, gdpPerCapita: 46125, gdpGrowthPct: 1.1,
    military: {
      activeTroops: 149, reserveTroops: 37,
      aircraftCarriers: 2, submarines: 10, destroyersFrigates: 20,
      combatAircraft: 693, tanks: 227, artilleryPieces: 89,
      navalVesselsTotal: 75, nuclearWarheads: 225, budgetBillionUSD: 74, globalFirepowerRank: 7,
    },
    nuclearStatus: 'possesses',
    leader: {
      name: 'Keir Starmer', nameKo: '키어 스타머', title: 'Prime Minister',
      sinceYear: 2024, age: 62,
      riskLevel: 'low',
      riskNotes: 'Labour majority government. Post-Brexit realignment with EU. Ukraine strong supporter. Defence spending increase pledge.',
      successionRisk: 'low',
    },
    alliances: ['NATO', 'G7', 'G20', 'AUKUS', 'Five Eyes', 'UN P5'],
    geopoliticalStance: 'western',
    strategicObjectives: [
      'Maintain NATO Article 5 commitments',
      'Ukraine support (weapons + training)',
      'AUKUS submarine deal',
      'Post-Brexit global trade reset',
      'Indo-Pacific tilt (carrier deployments)',
    ],
    instabilityRisk: 'low', powerIndex: 68,
  },
  {
    code: 'FR', name: 'France', nameKo: '프랑스',
    flagEmoji: '🇫🇷', capitalCity: 'Paris',
    capitalLon: 2.35, capitalLat: 48.85,
    population: 68, gdpBillionUSD: 3031, gdpPerCapita: 44551, gdpGrowthPct: 1.1,
    military: {
      activeTroops: 203, reserveTroops: 35,
      aircraftCarriers: 1, submarines: 10, destroyersFrigates: 20,
      combatAircraft: 1045, tanks: 222, artilleryPieces: 109,
      navalVesselsTotal: 103, nuclearWarheads: 290, budgetBillionUSD: 63, globalFirepowerRank: 9,
    },
    nuclearStatus: 'possesses',
    leader: {
      name: 'Emmanuel Macron', nameKo: '에마뉘엘 마크롱', title: 'President',
      sinceYear: 2017, age: 48,
      riskLevel: 'medium',
      riskNotes: 'Lost parliamentary majority 2024. Far-right RN surge. Calling for EU strategic autonomy. Proposed nuclear umbrella for Europe.',
      successionRisk: 'medium',
    },
    alliances: ['NATO', 'G7', 'G20', 'EU', 'UN P5'],
    geopoliticalStance: 'western',
    strategicObjectives: [
      'EU strategic autonomy from US',
      'European nuclear deterrence leadership',
      'Sahel military rebalancing (post-Wagner)',
      'Ukraine diplomatic + military support',
      'Indo-Pacific presence (Polynesia/New Caledonia)',
    ],
    instabilityRisk: 'medium', powerIndex: 66,
  },

  // ── NATO / Western Allies ─────────────────────────────────────────────────
  {
    code: 'DE', name: 'Germany', nameKo: '독일',
    flagEmoji: '🇩🇪', capitalCity: 'Berlin',
    capitalLon: 13.40, capitalLat: 52.52,
    population: 84, gdpBillionUSD: 4456, gdpPerCapita: 53009, gdpGrowthPct: -0.2,
    military: {
      activeTroops: 181, reserveTroops: 30,
      aircraftCarriers: 0, submarines: 5, destroyersFrigates: 14,
      combatAircraft: 623, tanks: 312, artilleryPieces: 121,
      navalVesselsTotal: 65, budgetBillionUSD: 97, globalFirepowerRank: 19,
    },
    nuclearStatus: 'umbrella',
    leader: {
      name: 'Friedrich Merz', nameKo: '프리드리히 메르츠', title: 'Chancellor',
      sinceYear: 2025, age: 70,
      riskLevel: 'low',
      riskNotes: 'CDU-led coalition. Zeitenwende rearmament; €500B defence fund. Highest military budget since WWII.',
      successionRisk: 'low',
    },
    alliances: ['NATO', 'G7', 'G20', 'EU'],
    geopoliticalStance: 'western',
    strategicObjectives: ['Rebuild Bundeswehr to NATO standard', 'Phase-out Russian energy dependency', 'EU leadership post-Brexit'],
    instabilityRisk: 'low', powerIndex: 62,
  },
  {
    code: 'PL', name: 'Poland', nameKo: '폴란드',
    flagEmoji: '🇵🇱', capitalCity: 'Warsaw',
    capitalLon: 21.01, capitalLat: 52.23,
    population: 38, gdpBillionUSD: 811, gdpPerCapita: 21342, gdpGrowthPct: 2.9,
    military: {
      activeTroops: 164, reserveTroops: 75,
      aircraftCarriers: 0, submarines: 5, destroyersFrigates: 2,
      combatAircraft: 234, tanks: 1049, artilleryPieces: 600,
      navalVesselsTotal: 47, budgetBillionUSD: 29, globalFirepowerRank: 20,
    },
    nuclearStatus: 'umbrella',
    leader: {
      name: 'Donald Tusk', nameKo: '도날드 투스크', title: 'Prime Minister',
      sinceYear: 2023, age: 68,
      riskLevel: 'low',
      riskNotes: 'Pro-EU coalition. Highest NATO spending % in alliance (4%+). Front-line state. Aggressively rearming.',
      successionRisk: 'low',
    },
    alliances: ['NATO', 'G20', 'EU'],
    geopoliticalStance: 'western',
    strategicObjectives: ['Eastern flank NATO hub', 'Absorb US forces (Fort Trump)', 'Rearm to regional power', 'Ukraine corridor support'],
    instabilityRisk: 'low', powerIndex: 42,
  },

  // ── Asia-Pacific ──────────────────────────────────────────────────────────
  {
    code: 'JP', name: 'Japan', nameKo: '일본',
    flagEmoji: '🇯🇵', capitalCity: 'Tokyo',
    capitalLon: 139.69, capitalLat: 35.68,
    population: 124, gdpBillionUSD: 4213, gdpPerCapita: 33834, gdpGrowthPct: 1.9,
    military: {
      activeTroops: 247, reserveTroops: 56,
      aircraftCarriers: 2, submarines: 22, destroyersFrigates: 46,
      combatAircraft: 748, tanks: 1004, artilleryPieces: 307,
      navalVesselsTotal: 155, budgetBillionUSD: 72, globalFirepowerRank: 7,
    },
    nuclearStatus: 'umbrella',
    leader: {
      name: 'Sanae Takaichi', nameKo: '다카이치 사나에', title: 'Prime Minister',
      sinceYear: 2025, age: 64,
      riskLevel: 'low',
      riskNotes: 'LDP government. Record defence budget (2% GDP). Offensive strike capability policy change. F-35 + Tomahawk acquisition.',
      successionRisk: 'low',
    },
    alliances: ['US-Japan Alliance', 'QUAD', 'G7', 'G20'],
    geopoliticalStance: 'western',
    strategicObjectives: ['Counter China ADIZ incursions', 'DPRK missile/nuke threat', 'Develop strike capability (Article 9 reinterpretation)', 'Taiwan contingency'],
    instabilityRisk: 'low', powerIndex: 64,
  },
  {
    code: 'KR', name: 'South Korea', nameKo: '대한민국',
    flagEmoji: '🇰🇷', capitalCity: 'Seoul',
    capitalLon: 126.98, capitalLat: 37.57,
    population: 52, gdpBillionUSD: 1713, gdpPerCapita: 33147, gdpGrowthPct: 2.3,
    military: {
      activeTroops: 555, reserveTroops: 3100,
      aircraftCarriers: 0, submarines: 22, destroyersFrigates: 24,
      combatAircraft: 1561, tanks: 2300, artilleryPieces: 5800,
      navalVesselsTotal: 166, budgetBillionUSD: 55, globalFirepowerRank: 5,
    },
    nuclearStatus: 'umbrella',
    leader: {
      name: 'Lee Jae-myung', nameKo: '이재명', title: 'President',
      sinceYear: 2025, age: 61,
      riskLevel: 'medium',
      riskNotes: 'Progressive government. Martial law crisis Dec 2024. USFK cost-sharing renegotiation. Engagement vs deterrence tension with DPRK.',
      successionRisk: 'medium',
    },
    alliances: ['US-ROK Alliance', 'G20', 'QUAD+'],
    geopoliticalStance: 'western',
    strategicObjectives: ['DPRK nuclear deterrence', 'Sustain USFK', 'Chip/battery supply chain security', 'Peninsula unification framework'],
    instabilityRisk: 'medium', powerIndex: 56,
  },
  {
    code: 'IN', name: 'India', nameKo: '인도',
    flagEmoji: '🇮🇳', capitalCity: 'New Delhi',
    capitalLon: 77.21, capitalLat: 28.61,
    population: 1440, gdpBillionUSD: 3730, gdpPerCapita: 2591, gdpGrowthPct: 6.5,
    military: {
      activeTroops: 1455, reserveTroops: 1155,
      aircraftCarriers: 2, submarines: 16, destroyersFrigates: 25,
      combatAircraft: 2182, tanks: 4614, artilleryPieces: 4060,
      navalVesselsTotal: 285, nuclearWarheads: 172, budgetBillionUSD: 81, globalFirepowerRank: 4,
    },
    nuclearStatus: 'possesses',
    leader: {
      name: 'Narendra Modi', nameKo: '나렌드라 모디', title: 'Prime Minister',
      sinceYear: 2014, age: 75,
      riskLevel: 'low',
      riskNotes: 'Third term coalition. "Strategic Autonomy" doctrine — buys Russian oil while in QUAD. Border tensions with China (LAC). Pakistan nuclear standoff.',
      successionRisk: 'low',
    },
    alliances: ['QUAD', 'G20', 'BRICS', 'SCO', 'Non-Aligned (de facto)'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: ['Balance China (QUAD) + retain Russia access', 'Become #3 global economy by 2030', 'Nuclear-armed regional dominance', 'Control Indian Ocean lanes'],
    instabilityRisk: 'low', powerIndex: 74,
  },
  {
    code: 'KP', name: 'North Korea', nameKo: '북한',
    flagEmoji: '🇰🇵', capitalCity: 'Pyongyang',
    capitalLon: 125.75, capitalLat: 39.03,
    population: 26, gdpBillionUSD: 18, gdpPerCapita: 700, gdpGrowthPct: -1.5,
    military: {
      activeTroops: 1280, reserveTroops: 600,
      aircraftCarriers: 0, submarines: 35, destroyersFrigates: 0,
      combatAircraft: 545, tanks: 3500, artilleryPieces: 14000,
      navalVesselsTotal: 492, nuclearWarheads: 50, budgetBillionUSD: 4, globalFirepowerRank: 36,
    },
    nuclearStatus: 'possesses',
    leader: {
      name: 'Kim Jong-un', nameKo: '김정은', title: 'Supreme Leader / General Secretary',
      sinceYear: 2011, age: 41,
      riskLevel: 'critical',
      riskNotes: 'Tested ICBM capable of reaching US mainland. Deployed troops to Russia (Ukraine). Sister Kim Yo-jong major power. Health/weight concerns. No clear succession announced.',
      successionRisk: 'critical',
    },
    alliances: ['Russia (de facto)', 'China (patron)'],
    geopoliticalStance: 'rogue',
    strategicObjectives: ['Develop nuclear ICBM deterrent vs US', 'Extract concessions without denuclearization', 'Earn hard currency (cyber theft, weapons sales)', 'Regime survival at all costs'],
    instabilityRisk: 'high', powerIndex: 32,
  },
  {
    code: 'IR', name: 'Iran', nameKo: '이란',
    flagEmoji: '🇮🇷', capitalCity: 'Tehran',
    capitalLon: 51.39, capitalLat: 35.69,
    population: 87, gdpBillionUSD: 413, gdpPerCapita: 4745, gdpGrowthPct: 4.7,
    military: {
      activeTroops: 610, reserveTroops: 350,
      aircraftCarriers: 0, submarines: 19, destroyersFrigates: 7,
      combatAircraft: 551, tanks: 1996, artilleryPieces: 1900,
      navalVesselsTotal: 398, budgetBillionUSD: 10, globalFirepowerRank: 17,
    },
    nuclearStatus: 'pursuing',
    leader: {
      name: 'Masoud Pezeshkian', nameKo: '마수드 페제시키안', title: 'President',
      sinceYear: 2024, age: 70,
      riskLevel: 'high',
      riskNotes: 'Reformist president but Supreme Leader Khamenei controls IRGC & nuclear program. Nuclear enrichment at ~84%. Israeli preventive strike risk HIGH.',
      successionRisk: 'high',
    },
    alliances: ['Russia (tactical)', 'China (trade)', 'Axis of Resistance (Hezbollah, Houthi, Hamas, PMF)'],
    geopoliticalStance: 'rogue',
    strategicObjectives: ['Nuclear breakout capability (without crossing red line)', 'Regional hegemony via proxies', 'Undermine Israel/Saudi normalization', 'IRGC regional expansion'],
    instabilityRisk: 'high', powerIndex: 38,
  },
  {
    code: 'SA', name: 'Saudi Arabia', nameKo: '사우디아라비아',
    flagEmoji: '🇸🇦', capitalCity: 'Riyadh',
    capitalLon: 46.72, capitalLat: 24.69,
    population: 36, gdpBillionUSD: 1062, gdpPerCapita: 29820, gdpGrowthPct: 1.7,
    military: {
      activeTroops: 227, reserveTroops: 25,
      aircraftCarriers: 0, submarines: 0, destroyersFrigates: 7,
      combatAircraft: 867, tanks: 1142, artilleryPieces: 395,
      navalVesselsTotal: 55, budgetBillionUSD: 79, globalFirepowerRank: 16,
    },
    nuclearStatus: 'pursuing',
    leader: {
      name: 'Mohammad bin Salman', nameKo: '무함마드 빈 살만', title: 'Crown Prince / Prime Minister',
      sinceYear: 2017, age: 40,
      riskLevel: 'medium',
      riskNotes: 'De facto ruler. Vision 2030. Normalization with Israel on hold (Hamas Oct 7 effect). China-brokered Iran deal (2023). NEOM mega-project risks.',
      successionRisk: 'medium',
    },
    alliances: ['GCC', 'Arab League', 'G20', 'OPEC+'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: ['Vision 2030 economic diversification', 'Iran containment (nuclear concern)', 'Israel normalization (conditional)', 'Oil price stability via OPEC+'],
    instabilityRisk: 'medium', powerIndex: 44,
  },
  {
    code: 'IL', name: 'Israel', nameKo: '이스라엘',
    flagEmoji: '🇮🇱', capitalCity: 'Jerusalem',
    capitalLon: 35.22, capitalLat: 31.77,
    population: 9.7, gdpBillionUSD: 509, gdpPerCapita: 52525, gdpGrowthPct: 1.2,
    military: {
      activeTroops: 169, reserveTroops: 465,
      aircraftCarriers: 0, submarines: 5, destroyersFrigates: 3,
      combatAircraft: 612, tanks: 1370, artilleryPieces: 650,
      navalVesselsTotal: 65, nuclearWarheads: 90, budgetBillionUSD: 27, globalFirepowerRank: 17,
    },
    nuclearStatus: 'possesses',
    leader: {
      name: 'Benjamin Netanyahu', nameKo: '베냐민 네타냐후', title: 'Prime Minister',
      sinceYear: 2022, age: 76,
      riskLevel: 'high',
      riskNotes: 'Corruption trial ongoing. War cabinet tensions. Gaza Phase 2 uncertain. Iran strike timing constant pressure. ICC arrest warrant issued.',
      successionRisk: 'medium',
    },
    alliances: ['US (patron)', 'Abraham Accords (UAE/Bahrain/Morocco)', 'G7+'],
    geopoliticalStance: 'western',
    strategicObjectives: ['Neutralize Iran nuclear program (pre-emptive option)', 'Destroy Hamas governance', 'Hezbollah demilitarization', 'Gaza security buffer'],
    instabilityRisk: 'high', powerIndex: 46,
  },
  {
    code: 'TR', name: 'Turkey', nameKo: '터키',
    flagEmoji: '🇹🇷', capitalCity: 'Ankara',
    capitalLon: 32.86, capitalLat: 39.93,
    population: 85, gdpBillionUSD: 1108, gdpPerCapita: 13045, gdpGrowthPct: 4.5,
    military: {
      activeTroops: 355, reserveTroops: 379,
      aircraftCarriers: 0, submarines: 12, destroyersFrigates: 16,
      combatAircraft: 1068, tanks: 3022, artilleryPieces: 1907,
      navalVesselsTotal: 194, budgetBillionUSD: 21, globalFirepowerRank: 8,
    },
    nuclearStatus: 'umbrella',
    leader: {
      name: 'Recep Tayyip Erdoğan', nameKo: '레제프 타이이프 에르도안', title: 'President',
      sinceYear: 2003, age: 72,
      riskLevel: 'medium',
      riskNotes: 'NATO member but blocked Sweden (resolved). Buys Russian S-400. Hosts Hamas political office. Syria post-Assad influence expansion. Bosphorus control leverage.',
      successionRisk: 'medium',
    },
    alliances: ['NATO', 'G20'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: ['Bosphorus/Dardanelles strategic leverage', 'Kurdish PKK containment in Syria/Iraq', 'Energy hub (TurkStream)', 'Regional power broker'],
    instabilityRisk: 'medium', powerIndex: 48,
  },
  {
    code: 'PK', name: 'Pakistan', nameKo: '파키스탄',
    flagEmoji: '🇵🇰', capitalCity: 'Islamabad',
    capitalLon: 73.06, capitalLat: 33.72,
    population: 231, gdpBillionUSD: 347, gdpPerCapita: 1508, gdpGrowthPct: 2.4,
    military: {
      activeTroops: 654, reserveTroops: 500,
      aircraftCarriers: 0, submarines: 5, destroyersFrigates: 10,
      combatAircraft: 1399, tanks: 2627, artilleryPieces: 4472,
      navalVesselsTotal: 114, nuclearWarheads: 170, budgetBillionUSD: 10, globalFirepowerRank: 9,
    },
    nuclearStatus: 'possesses',
    leader: {
      name: 'Shehbaz Sharif', nameKo: '샤바즈 샤리프', title: 'Prime Minister',
      sinceYear: 2024, age: 73,
      riskLevel: 'high',
      riskNotes: 'Imran Khan imprisoned. Civil-military tension high. Economic crisis (IMF bailout). TTP cross-border attacks. India nuclear standoff. China CPEC pressure.',
      successionRisk: 'high',
    },
    alliances: ['SCO', 'OIC', 'China (patron)', 'Saudi (economic)'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: ['India nuclear deterrence (Kashmir)', 'Afghan Taliban border management', 'CPEC Phase II completion', 'IMF economic stabilization'],
    instabilityRisk: 'high', powerIndex: 38,
  },
  {
    code: 'UA', name: 'Ukraine', nameKo: '우크라이나',
    flagEmoji: '🇺🇦', capitalCity: 'Kyiv',
    capitalLon: 30.52, capitalLat: 50.45,
    population: 38, gdpBillionUSD: 178, gdpPerCapita: 4630, gdpGrowthPct: 5.1,
    military: {
      activeTroops: 900, reserveTroops: 1000,
      aircraftCarriers: 0, submarines: 0, destroyersFrigates: 0,
      combatAircraft: 170, tanks: 1000, artilleryPieces: 2000,
      navalVesselsTotal: 10, budgetBillionUSD: 64, globalFirepowerRank: 18,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Volodymyr Zelensky', nameKo: '볼로디미르 젤렌스키', title: 'President',
      sinceYear: 2019, age: 47,
      riskLevel: 'critical',
      riskNotes: 'Active wartime president. Survived multiple Russian assassination attempts. NATO accession bid. Negotiation pressure from US/Trump administration.',
      successionRisk: 'critical',
    },
    alliances: ['NATO (candidate)', 'EU (candidate)', 'G7 (supported by)'],
    geopoliticalStance: 'western',
    strategicObjectives: ['Expel Russian forces from all territory incl. Crimea', 'NATO membership guarantee', 'EU accession', 'War crime prosecutions'],
    instabilityRisk: 'critical', powerIndex: 28,
  },
  {
    code: 'MM', name: 'Myanmar', nameKo: '미얀마',
    flagEmoji: '🇲🇲', capitalCity: 'Naypyidaw',
    capitalLon: 96.13, capitalLat: 19.75,
    population: 55, gdpBillionUSD: 58, gdpPerCapita: 1052, gdpGrowthPct: -2.1,
    military: {
      activeTroops: 406, reserveTroops: 100,
      aircraftCarriers: 0, submarines: 0, destroyersFrigates: 5,
      combatAircraft: 145, tanks: 334, artilleryPieces: 560,
      navalVesselsTotal: 149, budgetBillionUSD: 3, globalFirepowerRank: 40,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Min Aung Hlaing', nameKo: '민 아웅 흘라잉', title: 'SAC Chairman / PM',
      sinceYear: 2021, age: 68,
      riskLevel: 'critical',
      riskNotes: 'Feb 2021 coup. Sanctioned by US/EU/UK. ICC investigation. Losing territory to resistance. Junta losing cohesion. Chinese pressure to negotiate.',
      successionRisk: 'critical',
    },
    alliances: ['China (arms)', 'Russia (arms)'],
    geopoliticalStance: 'rogue',
    strategicObjectives: ['Suppress PDF resistance', 'Retain Chinese support', 'Control major cities', 'Regime survival'],
    instabilityRisk: 'critical', powerIndex: 18,
  },
  {
    code: 'AU', name: 'Australia', nameKo: '호주',
    flagEmoji: '🇦🇺', capitalCity: 'Canberra',
    capitalLon: 149.13, capitalLat: -35.28,
    population: 27, gdpBillionUSD: 1724, gdpPerCapita: 64040, gdpGrowthPct: 1.5,
    military: {
      activeTroops: 59, reserveTroops: 28,
      aircraftCarriers: 0, submarines: 6, destroyersFrigates: 12,
      combatAircraft: 452, tanks: 59, artilleryPieces: 70,
      navalVesselsTotal: 48, budgetBillionUSD: 35, globalFirepowerRank: 16,
    },
    nuclearStatus: 'umbrella',
    leader: {
      name: 'Anthony Albanese', nameKo: '앤서니 알바니즈', title: 'Prime Minister',
      sinceYear: 2022, age: 62,
      riskLevel: 'low',
      riskNotes: 'Labor government. AUKUS nuclear submarine deal (SSN-AUKUS). China trade war de-escalation. Pacific Islands influence competition vs China.',
      successionRisk: 'low',
    },
    alliances: ['AUKUS', 'QUAD', 'Five Eyes', 'G20'],
    geopoliticalStance: 'western',
    strategicObjectives: ['AUKUS nuclear submarine fleet', 'Pacific Islands geo-influence', 'China trade diversification', 'QUAD Indo-Pacific security'],
    instabilityRisk: 'low', powerIndex: 46,
  },

  // ── G20 Missing ───────────────────────────────────────────────────────────
  {
    code: 'BR', name: 'Brazil', nameKo: '브라질',
    flagEmoji: '🇧🇷', capitalCity: 'Brasília',
    capitalLon: -47.93, capitalLat: -15.78,
    population: 215, gdpBillionUSD: 2174, gdpPerCapita: 10100, gdpGrowthPct: 2.9,
    military: {
      activeTroops: 334, reserveTroops: 1340,
      aircraftCarriers: 0, submarines: 5, destroyersFrigates: 11,
      combatAircraft: 231, tanks: 469, artilleryPieces: 500,
      navalVesselsTotal: 108, budgetBillionUSD: 7, globalFirepowerRank: 12,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Luiz Inácio Lula da Silva', nameKo: '루이스 이나시우 룰라 다 시우바', title: 'President',
      sinceYear: 2023, age: 79,
      riskLevel: 'medium',
      riskNotes: 'Third term. BRICS expansion advocate. Amazon sovereignty. US-China balancing. Health concerns (brain surgery Dec 2024). Left-wing coalition management.',
      successionRisk: 'medium',
    },
    alliances: ['G20', 'BRICS', 'Mercosur', 'CELAC', 'UN'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: [
      'BRICS expansion + de-dollarization push',
      'Amazon sovereignty / climate diplomacy',
      'South-South trade diversification',
      'Regional leadership in Latin America',
      'Mercosur-EU trade deal finalization',
    ],
    instabilityRisk: 'medium', powerIndex: 48,
  },
  {
    code: 'CA', name: 'Canada', nameKo: '캐나다',
    flagEmoji: '🇨🇦', capitalCity: 'Ottawa',
    capitalLon: -75.70, capitalLat: 45.42,
    population: 40, gdpBillionUSD: 2140, gdpPerCapita: 53600, gdpGrowthPct: 1.5,
    military: {
      activeTroops: 67, reserveTroops: 30,
      aircraftCarriers: 0, submarines: 4, destroyersFrigates: 12,
      combatAircraft: 287, tanks: 82, artilleryPieces: 42,
      navalVesselsTotal: 60, budgetBillionUSD: 27, globalFirepowerRank: 27,
    },
    nuclearStatus: 'umbrella',
    leader: {
      name: 'Mark Carney', nameKo: '마크 카니', title: 'Prime Minister',
      sinceYear: 2025, age: 60,
      riskLevel: 'low',
      riskNotes: 'Former Bank of Canada/England governor. Liberal PM post-Trudeau. US trade war (tariffs) major challenge. Arctic sovereignty (Russia/China pressure). NATO 2% pledge.',
      successionRisk: 'low',
    },
    alliances: ['NATO', 'G7', 'G20', 'NORAD', 'Five Eyes', 'USMCA'],
    geopoliticalStance: 'western',
    strategicObjectives: [
      'US tariff negotiation / USMCA resilience',
      'Arctic sovereignty (Russia/China competition)',
      'NATO 2% GDP commitment',
      'NORAD modernization (missile defense)',
      'Indo-Pacific Strategy implementation',
    ],
    instabilityRisk: 'low', powerIndex: 54,
  },
  {
    code: 'IT', name: 'Italy', nameKo: '이탈리아',
    flagEmoji: '🇮🇹', capitalCity: 'Rome',
    capitalLon: 12.50, capitalLat: 41.89,
    population: 59, gdpBillionUSD: 2169, gdpPerCapita: 36900, gdpGrowthPct: 0.9,
    military: {
      activeTroops: 170, reserveTroops: 18,
      aircraftCarriers: 2, submarines: 8, destroyersFrigates: 12,
      combatAircraft: 764, tanks: 200, artilleryPieces: 150,
      navalVesselsTotal: 184, budgetBillionUSD: 35, globalFirepowerRank: 10,
    },
    nuclearStatus: 'umbrella',
    leader: {
      name: 'Giorgia Meloni', nameKo: '조르자 멜로니', title: 'Prime Minister',
      sinceYear: 2022, age: 48,
      riskLevel: 'low',
      riskNotes: 'Far-right FdI. Pro-NATO/Ukraine (surprised critics). G7 host 2024. Mediterranean migration pressure. Mattei Plan for Africa. Libya/Sahel energy diplomacy.',
      successionRisk: 'low',
    },
    alliances: ['NATO', 'G7', 'G20', 'EU'],
    geopoliticalStance: 'western',
    strategicObjectives: [
      'Mattei Plan for Africa (migration root causes)',
      'Mediterranean energy hub (Algeria/Libya gas)',
      'NATO southern flank leadership',
      'EU budget reform',
      'Libya/Tunisia stabilization',
    ],
    instabilityRisk: 'low', powerIndex: 52,
  },
  {
    code: 'MX', name: 'Mexico', nameKo: '멕시코',
    flagEmoji: '🇲🇽', capitalCity: 'Mexico City',
    capitalLon: -99.13, capitalLat: 19.43,
    population: 130, gdpBillionUSD: 1414, gdpPerCapita: 10900, gdpGrowthPct: 1.6,
    military: {
      activeTroops: 203, reserveTroops: 82,
      aircraftCarriers: 0, submarines: 0, destroyersFrigates: 4,
      combatAircraft: 160, tanks: 130, artilleryPieces: 100,
      navalVesselsTotal: 150, budgetBillionUSD: 9, globalFirepowerRank: 32,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Claudia Sheinbaum', nameKo: '클라우디아 세인바움', title: 'President',
      sinceYear: 2024, age: 62,
      riskLevel: 'medium',
      riskNotes: 'First female president. Morena party AMLO successor. US tariff war (Trump). Cartel violence reshaping security. Nearshoring manufacturing opportunity. Fentanyl trafficking US pressure.',
      successionRisk: 'low',
    },
    alliances: ['G20', 'USMCA', 'CELAC', 'OAS'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: [
      'Navigate US tariff pressure (USMCA)',
      'Cartel/security crisis management',
      'Nearshoring FDI capture (China+1)',
      'Migration management (US pressure)',
      'PEMEX/energy re-nationalization',
    ],
    instabilityRisk: 'high', powerIndex: 44,
  },
  {
    code: 'ID', name: 'Indonesia', nameKo: '인도네시아',
    flagEmoji: '🇮🇩', capitalCity: 'Jakarta',
    capitalLon: 106.85, capitalLat: -6.21,
    population: 278, gdpBillionUSD: 1371, gdpPerCapita: 4900, gdpGrowthPct: 5.0,
    military: {
      activeTroops: 395, reserveTroops: 400,
      aircraftCarriers: 0, submarines: 4, destroyersFrigates: 7,
      combatAircraft: 300, tanks: 500, artilleryPieces: 600,
      navalVesselsTotal: 150, budgetBillionUSD: 10, globalFirepowerRank: 15,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Prabowo Subianto', nameKo: '프라보워 수비안토', title: 'President',
      sinceYear: 2024, age: 74,
      riskLevel: 'medium',
      riskNotes: 'Former general and defence minister. Jokowi coalition support. Natuna Sea disputes with China. Active Non-Alignment. Nusantara new capital cost overruns. Papua separatism.',
      successionRisk: 'medium',
    },
    alliances: ['G20', 'ASEAN', 'Non-Aligned Movement', 'OIC'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: [
      'Nusantara new capital construction',
      'Nickel/battery supply chain dominance',
      'Natuna Sea (EEZ) defense vs China',
      'ASEAN leadership role',
      'US-China strategic balance',
    ],
    instabilityRisk: 'low', powerIndex: 50,
  },
  {
    code: 'AR', name: 'Argentina', nameKo: '아르헨티나',
    flagEmoji: '🇦🇷', capitalCity: 'Buenos Aires',
    capitalLon: -58.38, capitalLat: -34.60,
    population: 46, gdpBillionUSD: 640, gdpPerCapita: 13900, gdpGrowthPct: 5.0,
    military: {
      activeTroops: 74, reserveTroops: 0,
      aircraftCarriers: 0, submarines: 3, destroyersFrigates: 9,
      combatAircraft: 103, tanks: 200, artilleryPieces: 650,
      navalVesselsTotal: 55, budgetBillionUSD: 2, globalFirepowerRank: 33,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Javier Milei', nameKo: '하비에르 밀레이', title: 'President',
      sinceYear: 2023, age: 55,
      riskLevel: 'medium',
      riskNotes: 'Radical libertarian shock therapy. Chainsaw budget cuts. IMF deal renegotiation. BRICS rejection. Strong US/Israel alignment. 2024 recession; 2025 recovery signs. Deregulation experiment.',
      successionRisk: 'medium',
    },
    alliances: ['G20', 'OAS', 'Mercosur'],
    geopoliticalStance: 'western',
    strategicObjectives: [
      'IMF debt restructuring',
      'Hyperinflation stabilization',
      'State privatization / deregulation',
      'Lithium triangle export development',
      'Falklands sovereignty (ongoing claim)',
    ],
    instabilityRisk: 'medium', powerIndex: 34,
  },
  {
    code: 'ZA', name: 'South Africa', nameKo: '남아프리카',
    flagEmoji: '🇿🇦', capitalCity: 'Pretoria',
    capitalLon: 28.19, capitalLat: -25.75,
    population: 60, gdpBillionUSD: 405, gdpPerCapita: 6800, gdpGrowthPct: 0.7,
    military: {
      activeTroops: 78, reserveTroops: 15,
      aircraftCarriers: 0, submarines: 3, destroyersFrigates: 4,
      combatAircraft: 49, tanks: 195, artilleryPieces: 300,
      navalVesselsTotal: 70, budgetBillionUSD: 3, globalFirepowerRank: 35,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Cyril Ramaphosa', nameKo: '시릴 라마포사', title: 'President',
      sinceYear: 2018, age: 72,
      riskLevel: 'medium',
      riskNotes: 'ANC lost parliamentary majority 2024 → Government of National Unity (GNU) with DA. Load-shedding energy crisis. BRICS + ICJ/ICC Gaza case leadership. Land reform pressure. US tariffs (AGOA).',
      successionRisk: 'medium',
    },
    alliances: ['G20', 'BRICS', 'AU', 'SADC', 'Commonwealth'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: [
      'GNU coalition stability',
      'Energy crisis resolution (load-shedding)',
      'BRICS+ African leadership',
      'ICJ/ICC multilateral legal challenges',
      'Critical minerals export strategy',
    ],
    instabilityRisk: 'medium', powerIndex: 34,
  },

  // ── Critical Regional Powers ───────────────────────────────────────────────
  {
    code: 'TW', name: 'Taiwan', nameKo: '대만',
    flagEmoji: '🇹🇼', capitalCity: 'Taipei',
    capitalLon: 121.56, capitalLat: 25.05,
    population: 23, gdpBillionUSD: 756, gdpPerCapita: 32000, gdpGrowthPct: 3.1,
    military: {
      activeTroops: 169, reserveTroops: 1660,
      aircraftCarriers: 0, submarines: 4, destroyersFrigates: 26,
      combatAircraft: 450, tanks: 900, artilleryPieces: 1000,
      navalVesselsTotal: 90, budgetBillionUSD: 19, globalFirepowerRank: 24,
    },
    nuclearStatus: 'umbrella',
    leader: {
      name: 'Lai Ching-te', nameKo: '라이 칭터', title: 'President',
      sinceYear: 2024, age: 65,
      riskLevel: 'high',
      riskNotes: 'DPP independence-leaning. Beijing labels him "separatist." PLA military exercises around Taiwan ongoing. US arms sales (Abrams, HIMARS). TSMC semiconductor as strategic deterrent.',
      successionRisk: 'medium',
    },
    alliances: ['US (TRA/informal)', 'QUAD (informal)', 'AUKUS (observer)'],
    geopoliticalStance: 'western',
    strategicObjectives: [
      'Defend sovereignty against PLA invasion',
      'TSMC as geostrategic deterrent ("silicon shield")',
      'US security guarantees + arms',
      'Diplomatic recognition expansion',
      'Asymmetric porcupine defense strategy',
    ],
    instabilityRisk: 'critical', powerIndex: 38,
  },
  {
    code: 'VN', name: 'Vietnam', nameKo: '베트남',
    flagEmoji: '🇻🇳', capitalCity: 'Hanoi',
    capitalLon: 105.85, capitalLat: 21.03,
    population: 98, gdpBillionUSD: 430, gdpPerCapita: 4300, gdpGrowthPct: 5.1,
    military: {
      activeTroops: 482, reserveTroops: 5000,
      aircraftCarriers: 0, submarines: 6, destroyersFrigates: 3,
      combatAircraft: 150, tanks: 1500, artilleryPieces: 3000,
      navalVesselsTotal: 65, budgetBillionUSD: 7, globalFirepowerRank: 22,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'To Lam', nameKo: '또 럼', title: 'General Secretary',
      sinceYear: 2024, age: 67,
      riskLevel: 'low',
      riskNotes: 'Former security chief. "Bamboo diplomacy" — comprehensive strategic partners with US, China, Russia, EU simultaneously. South China Sea disputes with China. Apple/Samsung manufacturing hub.',
      successionRisk: 'medium',
    },
    alliances: ['ASEAN', 'Non-Aligned Movement', 'CPTPP', 'RCEP'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: [
      'South China Sea sovereignty (Spratly/Paracel)',
      'Bamboo diplomacy — balance US/China',
      'Manufacturing FDI (China+1 strategy)',
      'CPTPP + EU FTA export growth',
      'Military modernization (Kilo submarines)',
    ],
    instabilityRisk: 'low', powerIndex: 36,
  },
  {
    code: 'PH', name: 'Philippines', nameKo: '필리핀',
    flagEmoji: '🇵🇭', capitalCity: 'Manila',
    capitalLon: 121.00, capitalLat: 14.60,
    population: 114, gdpBillionUSD: 437, gdpPerCapita: 3800, gdpGrowthPct: 5.5,
    military: {
      activeTroops: 137, reserveTroops: 131,
      aircraftCarriers: 0, submarines: 0, destroyersFrigates: 5,
      combatAircraft: 83, tanks: 0, artilleryPieces: 300,
      navalVesselsTotal: 111, budgetBillionUSD: 5, globalFirepowerRank: 34,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Ferdinand Marcos Jr.', nameKo: '페르디난드 마르코스 주니어', title: 'President',
      sinceYear: 2022, age: 68,
      riskLevel: 'medium',
      riskNotes: 'Bongbong Marcos. Pro-US pivot (4 new EDCA bases). South China Sea (WPS) confrontations with China escalating. VP Sara Duterte impeached. Abu Sayyaf/NPA insurgency.',
      successionRisk: 'medium',
    },
    alliances: ['US-Philippines MDT', 'ASEAN', 'QUAD+'],
    geopoliticalStance: 'western',
    strategicObjectives: [
      'West Philippine Sea sovereignty vs China',
      'US base expansion (EDCA sites)',
      'ASEAN central role',
      'Mindanao peace process',
      'BPO + OFW remittance economy',
    ],
    instabilityRisk: 'medium', powerIndex: 28,
  },
  {
    code: 'NG', name: 'Nigeria', nameKo: '나이지리아',
    flagEmoji: '🇳🇬', capitalCity: 'Abuja',
    capitalLon: 7.49, capitalLat: 9.06,
    population: 220, gdpBillionUSD: 477, gdpPerCapita: 2200, gdpGrowthPct: 3.3,
    military: {
      activeTroops: 223, reserveTroops: 0,
      aircraftCarriers: 0, submarines: 0, destroyersFrigates: 1,
      combatAircraft: 44, tanks: 360, artilleryPieces: 640,
      navalVesselsTotal: 27, budgetBillionUSD: 3, globalFirepowerRank: 35,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Bola Tinubu', nameKo: '볼라 티누부', title: 'President',
      sinceYear: 2023, age: 72,
      riskLevel: 'high',
      riskNotes: 'Fuel subsidy removal shock → naira devaluation 50%+. Boko Haram/ISWAP insurgency in north. Niger coup destabilizing ECOWAS. Separatist tensions. IMF structural adjustment pressure.',
      successionRisk: 'medium',
    },
    alliances: ['AU', 'ECOWAS', 'G20', 'Commonwealth'],
    geopoliticalStance: 'western',
    strategicObjectives: [
      'Economic stabilization (naira/IMF)',
      'Boko Haram/ISWAP counterterrorism',
      'ECOWAS stability (Niger/Mali juntas)',
      'Oil production recovery (Dangote refinery)',
      'Largest Africa economy leadership',
    ],
    instabilityRisk: 'high', powerIndex: 30,
  },
  {
    code: 'EG', name: 'Egypt', nameKo: '이집트',
    flagEmoji: '🇪🇬', capitalCity: 'Cairo',
    capitalLon: 31.25, capitalLat: 30.06,
    population: 106, gdpBillionUSD: 396, gdpPerCapita: 3700, gdpGrowthPct: 3.8,
    military: {
      activeTroops: 439, reserveTroops: 479,
      aircraftCarriers: 0, submarines: 4, destroyersFrigates: 9,
      combatAircraft: 1134, tanks: 4295, artilleryPieces: 3000,
      navalVesselsTotal: 315, budgetBillionUSD: 4, globalFirepowerRank: 14,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Abdel Fattah el-Sisi', nameKo: '압델 파타 엘시시', title: 'President',
      sinceYear: 2014, age: 71,
      riskLevel: 'medium',
      riskNotes: 'Military-backed government. Gaza war economic impact (Suez Canal revenue down). IMF emergency loan. Ethiopia GERD dam water dispute. Sinai ISIS insurgency. Political opposition suppressed.',
      successionRisk: 'medium',
    },
    alliances: ['Arab League', 'AU', 'Non-Aligned Movement'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: [
      'Gaza ceasefire mediation (key broker)',
      'Suez Canal revenue recovery',
      'GERD water rights negotiations vs Ethiopia',
      'IMF stabilization program',
      'Sinai peninsula counterterrorism',
    ],
    instabilityRisk: 'high', powerIndex: 36,
  },
  {
    code: 'AE', name: 'UAE', nameKo: '아랍에미리트',
    flagEmoji: '🇦🇪', capitalCity: 'Abu Dhabi',
    capitalLon: 54.37, capitalLat: 24.45,
    population: 10, gdpBillionUSD: 507, gdpPerCapita: 53000, gdpGrowthPct: 3.4,
    military: {
      activeTroops: 63, reserveTroops: 0,
      aircraftCarriers: 0, submarines: 0, destroyersFrigates: 2,
      combatAircraft: 361, tanks: 434, artilleryPieces: 200,
      navalVesselsTotal: 75, budgetBillionUSD: 23, globalFirepowerRank: 26,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Mohamed bin Zayed Al Nahyan', nameKo: '무함마드 빈 자이드 알 나히안', title: 'President',
      sinceYear: 2022, age: 64,
      riskLevel: 'low',
      riskNotes: 'MBZ. Stable monarchy. Abraham Accords (Israel ties strained post-Gaza). Hosting Iran talks. AI/tech hub (G42/ADNOC). China ties vs US F-35 pressure. Yemen war drawdown.',
      successionRisk: 'low',
    },
    alliances: ['GCC', 'Arab League', 'Abraham Accords', 'I2U2'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: [
      'Dubai/Abu Dhabi global business hub',
      'AI/tech sovereignty (G42, ADNOC)',
      'Regional mediation (Iran, Israel, Sudan)',
      'Economic diversification (Vision 2031)',
      'US military partnership (Al Dhafra base)',
    ],
    instabilityRisk: 'low', powerIndex: 40,
  },
  {
    code: 'SG', name: 'Singapore', nameKo: '싱가포르',
    flagEmoji: '🇸🇬', capitalCity: 'Singapore',
    capitalLon: 103.82, capitalLat: 1.35,
    population: 6, gdpBillionUSD: 501, gdpPerCapita: 87900, gdpGrowthPct: 1.1,
    military: {
      activeTroops: 72, reserveTroops: 312,
      aircraftCarriers: 0, submarines: 4, destroyersFrigates: 12,
      combatAircraft: 110, tanks: 96, artilleryPieces: 60,
      navalVesselsTotal: 40, budgetBillionUSD: 15, globalFirepowerRank: 21,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Lawrence Wong', nameKo: '로런스 웡', title: 'Prime Minister',
      sinceYear: 2024, age: 52,
      riskLevel: 'low',
      riskNotes: 'PAP succession from Lee Hsien Loong. Technocratic governance. US-China hedging policy master. Key financial hub. Semiconductor supply chain node. South China Sea freedom of navigation.',
      successionRisk: 'low',
    },
    alliances: ['ASEAN', 'Five Power Defence', 'CPTPP', 'RCEP'],
    geopoliticalStance: 'western',
    strategicObjectives: [
      'US-China strategic hedging (neutrality)',
      'Global financial center status',
      'Semiconductor + pharma hub',
      'ASEAN unity / multilateral order',
      'Water + food security',
    ],
    instabilityRisk: 'low', powerIndex: 38,
  },
  {
    code: 'MY', name: 'Malaysia', nameKo: '말레이시아',
    flagEmoji: '🇲🇾', capitalCity: 'Kuala Lumpur',
    capitalLon: 101.69, capitalLat: 3.15,
    population: 33, gdpBillionUSD: 440, gdpPerCapita: 13400, gdpGrowthPct: 4.4,
    military: {
      activeTroops: 113, reserveTroops: 51,
      aircraftCarriers: 0, submarines: 2, destroyersFrigates: 11,
      combatAircraft: 55, tanks: 48, artilleryPieces: 200,
      navalVesselsTotal: 60, budgetBillionUSD: 5, globalFirepowerRank: 44,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Anwar Ibrahim', nameKo: '안와르 이브라힘', title: 'Prime Minister',
      sinceYear: 2022, age: 77,
      riskLevel: 'medium',
      riskNotes: 'Long-time opposition leader finally PM. Multi-ethnic coalition management (Pakatan Harapan + BN). South China Sea overlapping claims with China. ASEAN chair 2025. Islamic finance hub.',
      successionRisk: 'medium',
    },
    alliances: ['ASEAN', 'OIC', 'Commonwealth', 'RCEP', 'CPTPP'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: [
      'South China Sea EEZ defense',
      'ASEAN 2025 chairmanship agenda',
      'US-China trade balance',
      'Islamic finance + halal hub',
      'EV + semiconductor manufacturing',
    ],
    instabilityRisk: 'low', powerIndex: 28,
  },
  {
    code: 'TH', name: 'Thailand', nameKo: '태국',
    flagEmoji: '🇹🇭', capitalCity: 'Bangkok',
    capitalLon: 100.50, capitalLat: 13.75,
    population: 71, gdpBillionUSD: 548, gdpPerCapita: 7700, gdpGrowthPct: 1.9,
    military: {
      activeTroops: 361, reserveTroops: 200,
      aircraftCarriers: 1, submarines: 0, destroyersFrigates: 10,
      combatAircraft: 188, tanks: 725, artilleryPieces: 1000,
      navalVesselsTotal: 81, budgetBillionUSD: 8, globalFirepowerRank: 28,
    },
    nuclearStatus: 'none',
    leader: {
      name: 'Paetongtarn Shinawatra', nameKo: '패텅턴 시나왓', title: 'Prime Minister',
      sinceYear: 2024, age: 38,
      riskLevel: 'medium',
      riskNotes: 'Daughter of Thaksin Shinawatra (returned from exile). Move Forward/Pheu Thai political dynamics. Military-civilian balance. Myanmar border scam compound crisis. Tourism recovery.',
      successionRisk: 'medium',
    },
    alliances: ['ASEAN', 'US-Thailand MDT', 'RCEP'],
    geopoliticalStance: 'pivotal',
    strategicObjectives: [
      'US-China balance (pivot zone)',
      'Tourism sector full recovery',
      'EV manufacturing hub (Chinese FDI)',
      'Myanmar border stability + scam crackdown',
      'Mekong water cooperation',
    ],
    instabilityRisk: 'medium', powerIndex: 32,
  },
];

// ---------------------------------------------------------------------------
// Compact helper — builds basic CountryProfile for non-G20 countries
// Provides default values for all required fields (military = zeros, leader = TBD)
// [code, flag, name, capital, lon, lat, pop(M), gdp($B), gdpPC, growth%, stance, risk, powerIdx]
// ---------------------------------------------------------------------------
import type { GeopoliticalStance } from '@/types';
import type { ThreatLevel } from '@/types';

type CompactRow = [
  string, string, string, string,
  number, number,
  number, number, number, number,
  GeopoliticalStance, ThreatLevel, number
];

const DEFAULT_MILITARY = {
  activeTroops: 0, reserveTroops: 0, aircraftCarriers: 0, submarines: 0,
  destroyersFrigates: 0, combatAircraft: 0, tanks: 0, artilleryPieces: 0,
  navalVesselsTotal: 0, budgetBillionUSD: 0,
};

function mk(r: CompactRow): CountryProfile {
  const [code, flag, name, capital, lon, lat, pop, gdp, gdpPc, growth, stance, risk, pi] = r;
  return {
    code, flagEmoji: flag, name, capitalCity: capital,
    capitalLon: lon, capitalLat: lat,
    population: pop, gdpBillionUSD: gdp, gdpPerCapita: gdpPc, gdpGrowthPct: growth,
    geopoliticalStance: stance, instabilityRisk: risk, powerIndex: pi,
    military: { ...DEFAULT_MILITARY },
    nuclearStatus: 'none',
    leader: { name: '—', title: '—', sinceYear: 2024, age: 0, riskLevel: 'info', riskNotes: 'Intel limited', successionRisk: 'info' },
    alliances: [],
    strategicObjectives: [],
  };
}

const BASIC_COUNTRIES: CountryProfile[] = ([
  // ── Europe ──────────────────────────────────────────────────────────────
  ['ES','🇪🇸','Spain','Madrid',-3.70,40.42, 47,1580,33500,2.5,'western','low',38],
  ['PL','🇵🇱','Poland','Warsaw',21.01,52.23, 38,842,22300,2.9,'western','low',32],
  ['NL','🇳🇱','Netherlands','Amsterdam',4.90,52.37, 17,1118,64700,1.8,'western','low',34],
  ['SE','🇸🇪','Sweden','Stockholm',18.06,59.33, 10,593,54600,0.5,'western','low',30],
  ['NO','🇳🇴','Norway','Oslo',10.74,59.91, 5,546,106600,2.1,'western','low',28],
  ['BE','🇧🇪','Belgium','Brussels',4.35,50.85, 11,627,54400,1.5,'western','low',28],
  ['CH','🇨🇭','Switzerland','Bern',7.45,46.95, 9,905,98600,1.6,'western','low',30],
  ['AT','🇦🇹','Austria','Vienna',16.37,48.21, 9,526,57600,0.8,'western','low',24],
  ['PT','🇵🇹','Portugal','Lisbon',-9.14,38.74, 10,274,26700,2.3,'western','low',20],
  ['DK','🇩🇰','Denmark','Copenhagen',12.57,55.68, 6,406,69000,1.8,'western','low',24],
  ['FI','🇫🇮','Finland','Helsinki',25.00,60.17, 5,305,55200,0.3,'western','low',22],
  ['GR','🇬🇷','Greece','Athens',23.72,37.98, 10,222,21700,2.1,'western','low',18],
  ['CZ','🇨🇿','Czech Republic','Prague',14.42,50.09, 11,330,30700,1.2,'western','low',20],
  ['RO','🇷🇴','Romania','Bucharest',26.10,44.44, 19,349,18600,2.4,'western','low',18],
  ['HU','🇭🇺','Hungary','Budapest',19.04,47.50, 10,212,21900,0.9,'pivotal','low',16],
  ['UA','🇺🇦','Ukraine','Kyiv',30.52,50.45, 44,178,4300,-4.0,'western','critical',22],
  ['BY','🇧🇾','Belarus','Minsk',27.57,53.90, 9,79,8600,3.9,'eastern','high',12],
  ['RS','🇷🇸','Serbia','Belgrade',20.46,44.80, 7,74,10600,2.7,'pivotal','medium',12],
  ['HR','🇭🇷','Croatia','Zagreb',15.98,45.82, 4,82,20300,2.5,'western','low',10],
  ['SK','🇸🇰','Slovakia','Bratislava',17.11,48.15, 5,128,23700,1.3,'western','low',12],
  ['BG','🇧🇬','Bulgaria','Sofia',23.32,42.70, 6,96,14200,1.8,'western','low',10],
  ['SI','🇸🇮','Slovenia','Ljubljana',14.51,46.05, 2,68,32600,2.0,'western','low',8],
  ['LT','🇱🇹','Lithuania','Vilnius',25.28,54.69, 3,77,27400,2.1,'western','low',8],
  ['LV','🇱🇻','Latvia','Riga',24.11,56.95, 2,43,23000,1.5,'western','low',7],
  ['EE','🇪🇪','Estonia','Tallinn',24.75,59.44, 1,42,31900,2.2,'western','low',7],
  ['IE','🇮🇪','Ireland','Dublin',-6.27,53.33, 5,597,117200,5.2,'western','low',22],
  ['IS','🇮🇸','Iceland','Reykjavik',-21.94,64.15, 0.4,26,67800,3.8,'western','low',6],
  ['AL','🇦🇱','Albania','Tirana',19.82,41.33, 3,22,7700,3.3,'western','low',6],
  ['MK','🇲🇰','North Macedonia','Skopje',21.43,42.00, 2,14,6700,2.4,'western','low',4],
  ['MD','🇲🇩','Moldova','Chișinău',28.86,47.01, 3,15,4200,1.8,'pivotal','high',4],
  ['AM','🇦🇲','Armenia','Yerevan',44.51,40.18, 3,20,6800,8.7,'pivotal','high',5],
  ['AZ','🇦🇿','Azerbaijan','Baku',49.87,40.41, 10,78,7800,8.5,'pivotal','medium',10],
  ['GE','🇬🇪','Georgia','Tbilisi',44.83,41.69, 4,28,7600,6.4,'pivotal','medium',7],
  // ── Middle East & North Africa ───────────────────────────────────────────
  ['IR','🇮🇷','Iran','Tehran',51.39,35.69, 89,401,4600,5.0,'rogue','high',42],
  ['IL','🇮🇱','Israel','Jerusalem',35.22,31.77, 9,559,58100,1.5,'western','high',40],
  ['IQ','🇮🇶','Iraq','Baghdad',44.39,33.34, 42,264,6300,4.2,'pivotal','high',16],
  ['SY','🇸🇾','Syria','Damascus',36.29,33.51, 22,60,2800,1.0,'pivotal','critical',8],
  ['YE','🇾🇪','Yemen','Sana\'a',44.21,15.35, 34,28,800,-2.0,'rogue','critical',4],
  ['JO','🇯🇴','Jordan','Amman',35.94,31.96, 10,50,5000,2.7,'western','medium',10],
  ['LB','🇱🇧','Lebanon','Beirut',35.50,33.89, 5,22,3700,-3.0,'pivotal','critical',6],
  ['AE','🇦🇪','UAE','Abu Dhabi',54.37,24.45, 10,507,53000,3.4,'pivotal','low',30],
  ['QA','🇶🇦','Qatar','Doha',51.54,25.29, 3,235,83500,2.1,'pivotal','low',20],
  ['KW','🇰🇼','Kuwait','Kuwait City',47.98,29.37, 5,136,29700,3.5,'western','low',14],
  ['BH','🇧🇭','Bahrain','Manama',50.58,26.22, 2,44,23800,3.6,'western','low',8],
  ['OM','🇴🇲','Oman','Muscat',58.59,23.61, 4,104,26000,1.3,'pivotal','low',12],
  ['LY','🇱🇾','Libya','Tripoli',13.19,32.90, 7,53,7500,7.5,'rogue','critical',8],
  ['TN','🇹🇳','Tunisia','Tunis',10.18,36.82, 12,49,4200,0.4,'pivotal','high',8],
  ['MA','🇲🇦','Morocco','Rabat',-6.84,33.99, 37,143,3900,3.2,'western','low',12],
  ['DZ','🇩🇿','Algeria','Algiers',3.06,36.74, 45,239,5400,4.1,'pivotal','medium',18],
  ['EG','🇪🇬','Egypt','Cairo',31.25,30.06, 106,396,3700,3.8,'pivotal','high',28],
  // ── Sub-Saharan Africa ───────────────────────────────────────────────────
  ['NG','🇳🇬','Nigeria','Abuja',7.49,9.06, 220,477,2200,3.3,'pivotal','high',22],
  ['ET','🇪🇹','Ethiopia','Addis Ababa',38.75,9.02, 126,156,1200,7.2,'pivotal','high',14],
  ['CD','🇨🇩','DR Congo','Kinshasa',15.31,-4.32, 102,65,640,6.5,'pivotal','critical',8],
  ['TZ','🇹🇿','Tanzania','Dodoma',35.74,-6.17, 64,80,1300,5.3,'neutral','medium',8],
  ['KE','🇰🇪','Kenya','Nairobi',36.82,-1.29, 54,116,2200,5.0,'western','medium',12],
  ['GH','🇬🇭','Ghana','Accra',-0.19,5.55, 33,79,2400,3.5,'western','medium',10],
  ['SD','🇸🇩','Sudan','Khartoum',32.55,15.56, 46,46,1000,-2.0,'pivotal','critical',6],
  ['AO','🇦🇴','Angola','Luanda',13.23,-8.84, 35,113,3200,3.1,'pivotal','high',10],
  ['MZ','🇲🇿','Mozambique','Maputo',32.59,-25.96, 32,17,530,4.8,'neutral','high',4],
  ['MG','🇲🇬','Madagascar','Antananarivo',47.53,-18.91, 28,15,540,4.2,'neutral','high',3],
  ['CM','🇨🇲','Cameroon','Yaoundé',11.52,3.87, 28,47,1700,3.9,'pivotal','high',6],
  ['CI','🇨🇮','Ivory Coast','Yamoussoukro',-5.29,6.82, 27,71,2700,6.5,'western','medium',8],
  ['SN','🇸🇳','Senegal','Dakar',-17.44,14.71, 17,30,1800,5.9,'western','medium',6],
  ['ML','🇲🇱','Mali','Bamako',-7.99,12.65, 22,18,820,3.5,'pivotal','critical',4],
  ['ZW','🇿🇼','Zimbabwe','Harare',31.05,-17.83, 15,23,1500,3.0,'pivotal','critical',4],
  ['ZM','🇿🇲','Zambia','Lusaka',28.28,-15.42, 19,29,1500,2.3,'pivotal','medium',4],
  ['SL','🇸🇱','Sierra Leone','Freetown',-13.23,8.49, 8,5,650,4.0,'neutral','high',2],
  ['SO','🇸🇴','Somalia','Mogadishu',45.34,2.05, 18,10,600,3.0,'rogue','critical',2],
  ['LR','🇱🇷','Liberia','Monrovia',-10.80,6.31, 5,4,800,5.0,'western','high',2],
  // ── South & Central Asia ────────────────────────────────────────────────
  ['PK','🇵🇰','Pakistan','Islamabad',73.06,33.72, 230,373,1600,2.4,'pivotal','critical',32],
  ['BD','🇧🇩','Bangladesh','Dhaka',90.41,23.71, 173,456,2700,5.8,'pivotal','medium',18],
  ['VN','🇻🇳','Vietnam','Hanoi',105.85,21.03, 98,430,4300,5.1,'pivotal','low',24],
  ['TH','🇹🇭','Thailand','Bangkok',100.50,13.75, 71,548,7700,1.9,'pivotal','low',22],
  ['PH','🇵🇭','Philippines','Manila',121.00,14.60, 114,437,3800,5.5,'western','medium',18],
  ['MY','🇲🇾','Malaysia','Kuala Lumpur',101.69,3.15, 33,440,13400,4.4,'pivotal','low',20],
  ['SG','🇸🇬','Singapore','Singapore',103.82,1.35, 6,501,87900,1.1,'western','low',28],
  ['MM','🇲🇲','Myanmar','Naypyidaw',96.13,19.75, 54,65,1200,-0.5,'rogue','critical',8],
  ['KH','🇰🇭','Cambodia','Phnom Penh',104.92,11.55, 17,31,1800,5.6,'eastern','medium',6],
  ['LA','🇱🇦','Laos','Vientiane',102.60,17.97, 7,17,2400,4.0,'eastern','medium',4],
  ['NP','🇳🇵','Nepal','Kathmandu',85.32,27.71, 30,42,1400,5.5,'pivotal','medium',5],
  ['LK','🇱🇰','Sri Lanka','Colombo',79.85,6.92, 22,88,3900,3.1,'pivotal','medium',8],
  ['AF','🇦🇫','Afghanistan','Kabul',69.17,34.52, 41,14,340,-3.0,'rogue','critical',3],
  ['KZ','🇰🇿','Kazakhstan','Astana',71.43,51.18, 19,261,13600,4.8,'pivotal','medium',16],
  ['UZ','🇺🇿','Uzbekistan','Tashkent',69.29,41.30, 36,99,2800,6.4,'pivotal','medium',8],
  ['TM','🇹🇲','Turkmenistan','Ashgabat',58.38,37.95, 6,57,9600,6.3,'neutral','high',6],
  ['TJ','🇹🇯','Tajikistan','Dushanbe',68.78,38.56, 10,12,1200,7.4,'eastern','medium',3],
  ['KG','🇰🇬','Kyrgyzstan','Bishkek',74.61,42.87, 7,11,1600,6.2,'pivotal','medium',3],
  ['MN','🇲🇳','Mongolia','Ulaanbaatar',106.92,47.90, 3,20,6700,5.1,'neutral','low',4],
  ['KP','🇰🇵','North Korea','Pyongyang',125.75,39.02, 26,30,1200,0.0,'rogue','critical',18],
  ['TW','🇹🇼','Taiwan','Taipei',121.56,25.05, 23,756,32000,3.1,'western','critical',30],
  // ── Americas ────────────────────────────────────────────────────────────
  ['CO','🇨🇴','Colombia','Bogotá',-74.07,4.71, 52,363,7000,1.3,'western','medium',16],
  ['VE','🇻🇪','Venezuela','Caracas',-66.92,10.49, 29,97,3400,4.0,'rogue','critical',8],
  ['CL','🇨🇱','Chile','Santiago',-70.65,-33.46, 19,337,17900,2.3,'western','low',16],
  ['PE','🇵🇪','Peru','Lima',-77.04,-12.04, 33,272,8300,2.4,'western','medium',12],
  ['EC','🇪🇨','Ecuador','Quito',-78.52,-0.22, 18,118,6700,3.1,'western','medium',8],
  ['BO','🇧🇴','Bolivia','Sucre',-65.26,-19.03, 12,45,3800,2.8,'pivotal','medium',5],
  ['PY','🇵🇾','Paraguay','Asunción',-57.64,-25.29, 7,43,6100,4.2,'western','medium',5],
  ['UY','🇺🇾','Uruguay','Montevideo',-56.19,-34.90, 4,82,23700,0.4,'western','low',8],
  ['CU','🇨🇺','Cuba','Havana',-82.35,23.13, 11,28,2600,2.0,'rogue','high',6],
  ['GT','🇬🇹','Guatemala','Guatemala City',-90.52,14.63, 18,95,5300,3.5,'western','high',5],
  ['HN','🇭🇳','Honduras','Tegucigalpa',-87.21,14.10, 10,32,3200,3.4,'western','high',4],
  ['SV','🇸🇻','El Salvador','San Salvador',-89.19,13.69, 6,34,5400,3.2,'western','medium',4],
  ['NI','🇳🇮','Nicaragua','Managua',-86.29,12.14, 7,15,2200,4.0,'pivotal','high',3],
  ['CR','🇨🇷','Costa Rica','San José',-84.09,9.93, 5,68,13200,5.1,'western','low',7],
  ['PA','🇵🇦','Panama','Panama City',-79.52,8.99, 4,77,19000,5.0,'western','low',8],
  ['JM','🇯🇲','Jamaica','Kingston',-76.79,17.99, 3,17,5900,1.8,'western','medium',4],
  ['HT','🇭🇹','Haiti','Port-au-Prince',-72.34,18.54, 11,19,1700,-3.0,'neutral','critical',2],
  ['DO','🇩🇴','Dominican Republic','Santo Domingo',-69.90,18.48, 11,121,10900,5.0,'western','medium',6],
  ['TT','🇹🇹','Trinidad and Tobago','Port of Spain',-61.52,10.65, 1,27,19300,1.8,'western','low',5],
  // ── Oceania ─────────────────────────────────────────────────────────────
  ['NZ','🇳🇿','New Zealand','Wellington',174.78,-41.29, 5,249,48300,1.1,'western','low',16],
  ['PG','🇵🇬','Papua New Guinea','Port Moresby',147.19,-9.44, 10,30,3000,4.5,'western','medium',4],
  ['FJ','🇫🇯','Fiji','Suva',178.44,-18.14, 1,5,5600,2.8,'pivotal','medium',2],
  // ── Additional Asia-Pacific ──────────────────────────────────────────────
  ['BN','🇧🇳','Brunei','Bandar Seri Begawan',114.94,4.94, 0.4,17,40000,1.8,'western','low',4],
  ['TL','🇹🇱','Timor-Leste','Dili',125.58,-8.56, 1,3,3000,3.5,'western','medium',1],
  // ── Caribbean / Central America (more) ───────────────────────────────────
  ['BB','🇧🇧','Barbados','Bridgetown',-59.62,13.10, 0.3,6,18000,5.0,'western','low',2],
  ['BS','🇧🇸','Bahamas','Nassau',-77.34,25.05, 0.4,14,32000,2.5,'western','low',2],
  // ── Additional Africa ─────────────────────────────────────────────────────
  ['UG','🇺🇬','Uganda','Kampala',32.58,0.32, 47,50,1100,5.3,'pivotal','medium',6],
  ['BF','🇧🇫','Burkina Faso','Ouagadougou',-1.52,12.37, 23,20,900,3.8,'pivotal','critical',3],
  ['NE','🇳🇪','Niger','Niamey',2.11,13.51, 26,17,650,7.0,'pivotal','critical',3],
  ['GA','🇬🇦','Gabon','Libreville',9.45,0.39, 2,18,8400,3.0,'pivotal','medium',3],
  ['CG','🇨🇬','Republic of Congo','Brazzaville',15.28,-4.27, 6,12,2000,3.2,'pivotal','medium',3],
  ['MR','🇲🇷','Mauritania','Nouakchott',-15.98,18.08, 4,10,2300,4.7,'pivotal','medium',3],
  ['RW','🇷🇼','Rwanda','Kigali',30.06,-1.94, 14,14,1000,7.2,'western','medium',4],
  ['BI','🇧🇮','Burundi','Gitega',29.93,-3.38, 13,3,230,1.8,'neutral','critical',1],
  ['ZA','🇿🇦','South Africa','Pretoria',28.19,-25.75, 60,405,6800,0.7,'pivotal','medium',28], // already in G20, skip if duplicate
  ['NA','🇳🇦','Namibia','Windhoek',17.08,-22.56, 3,13,4400,3.3,'western','low',3],
  ['BW','🇧🇼','Botswana','Gaborone',25.91,-24.65, 3,19,6400,2.9,'western','low',4],
  ['TG','🇹🇬','Togo','Lomé',1.22,6.14, 9,8,900,6.3,'neutral','medium',2],
  ['BJ','🇧🇯','Benin','Porto-Novo',2.62,6.37, 13,19,1500,5.8,'western','medium',3],
  ['MW','🇲🇼','Malawi','Lilongwe',33.79,-13.97, 20,14,700,1.4,'neutral','high',2],
  // ── Additional Middle East ────────────────────────────────────────────────
  ['PS','🇵🇸','Palestine','Ramallah',35.20,31.90, 5,18,3600,-8.0,'neutral','critical',4],
] as CompactRow[]).map(mk);

// Merge: G20 detailed profiles first, then basic profiles for remaining countries
// Deduplicate by code (detailed always wins)
const G20_CODES = new Set(COUNTRY_PROFILES.map((p) => p.code));
const ALL_COUNTRY_PROFILES: CountryProfile[] = [
  ...COUNTRY_PROFILES,
  ...BASIC_COUNTRIES.filter((p) => !G20_CODES.has(p.code)),
];

export function fetchCountryProfiles(): CountryProfile[] {
  return ALL_COUNTRY_PROFILES;
}

export function getCountryProfile(code: string): CountryProfile | undefined {
  return COUNTRY_PROFILES.find((p) => p.code === code);
}

// Compute Global Power Index ranking
export function getCountriesByPowerIndex(): CountryProfile[] {
  return [...COUNTRY_PROFILES].sort((a, b) => b.powerIndex - a.powerIndex);
}
